import { useState, useCallback, useEffect, useMemo } from 'react';
import './minitutorial.css';

/**
 * MiniTutorial - компонент для мини-обучения на конкретных страницах
 * 
 * Показывает подсказки по использованию текущей страницы
 * Только для десктопной версии (экран > 768px)
 */

// Константа для определения десктопа
const DESKTOP_BREAKPOINT = 768;

export default function MiniTutorial({ steps, pageKey }) {
    const [isActive, setIsActive] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [hasBeenShown, setHasBeenShown] = useState(false);
    const [targetRect, setTargetRect] = useState(null);
    const [isDesktop, setIsDesktop] = useState(() => {
        if (typeof window === 'undefined') return true;
        return window.innerWidth > DESKTOP_BREAKPOINT;
    });

    // Отслеживаем изменение размера экрана
    useEffect(() => {
        const checkIsDesktop = () => {
            setIsDesktop(window.innerWidth > DESKTOP_BREAKPOINT);
        };

        checkIsDesktop();
        window.addEventListener('resize', checkIsDesktop);
        return () => window.removeEventListener('resize', checkIsDesktop);
    }, []);

    // Проверяем, показывался ли туториал ранее
    useEffect(() => {
        const storageKey = `mini_tutorial_${pageKey}`;
        const shown = sessionStorage.getItem(storageKey);
        if (shown) {
            setHasBeenShown(true);
        }
    }, [pageKey]);

    const currentStep = steps[currentStepIndex];
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === steps.length - 1;

    // Обновляем позицию целевого элемента
    useEffect(() => {
        if (!isActive || !currentStep?.target) {
            setTargetRect(null);
            return;
        }

        const updatePosition = () => {
            const element = document.querySelector(currentStep.target);
            if (element) {
                const rect = element.getBoundingClientRect();
                setTargetRect({
                    top: rect.top - 8,
                    left: rect.left - 8,
                    width: rect.width + 16,
                    height: rect.height + 16,
                });
            }
        };

        updatePosition();

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isActive, currentStep]);

    const startTutorial = useCallback(() => {
        setIsActive(true);
        setCurrentStepIndex(0);
    }, []);

    const stopTutorial = useCallback(() => {
        setIsActive(false);
        const storageKey = `mini_tutorial_${pageKey}`;
        sessionStorage.setItem(storageKey, 'true');
        setHasBeenShown(true);
    }, [pageKey]);

    const nextStep = useCallback(() => {
        if (currentStepIndex < steps.length - 1) {
            setCurrentStepIndex(prev => prev + 1);
        } else {
            stopTutorial();
        }
    }, [currentStepIndex, steps.length, stopTutorial]);

    const prevStep = useCallback(() => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex(prev => prev - 1);
        }
    }, [currentStepIndex]);

    // Получаем позицию подсказки
    const tooltipStyle = useMemo(() => {
        if (!targetRect) {
            return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
        }

        const position = currentStep?.position || 'bottom';
        const spacing = 20;

        let top, left, transform;

        switch (position) {
            case 'top':
                top = targetRect.top - spacing;
                left = targetRect.left + targetRect.width / 2;
                transform = 'translate(-50%, -100%)';
                break;
            case 'bottom':
                top = targetRect.top + targetRect.height + spacing;
                left = targetRect.left + targetRect.width / 2;
                transform = 'translate(-50%, 0)';
                break;
            case 'left':
                top = targetRect.top + targetRect.height / 2;
                left = targetRect.left - spacing;
                transform = 'translate(-100%, -50%)';
                break;
            case 'right':
                top = targetRect.top + targetRect.height / 2;
                left = targetRect.left + targetRect.width + spacing;
                transform = 'translate(0, -50%)';
                break;
            default:
                top = targetRect.top + targetRect.height + spacing;
                left = targetRect.left + targetRect.width / 2;
                transform = 'translate(-50%, 0)';
        }

        return { top: `${top}px`, left: `${left}px`, transform };
    }, [targetRect, currentStep]);

    const arrowClass = useMemo(() => {
        return `mini-tutorial-arrow-${currentStep?.position || 'bottom'}`;
    }, [currentStep]);

    const progressPercent = ((currentStepIndex + 1) / steps.length) * 100;

    // Не показываем на мобильных устройствах
    if (!isDesktop) {
        return null;
    }

    if (!isActive) {
        return (
            <button 
                className="mini-tutorial-help-btn"
                onClick={startTutorial}
                title="Как пользоваться этой страницей?"
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                {!hasBeenShown && <span className="help-badge">?</span>}
            </button>
        );
    }

    return (
        <div className="mini-tutorial-overlay">
            {/* Подсветка целевого элемента */}
            {targetRect && (
                <div 
                    className="mini-tutorial-highlight"
                    style={{
                        position: 'fixed',
                        top: targetRect.top,
                        left: targetRect.left,
                        width: targetRect.width,
                        height: targetRect.height,
                    }}
                >
                    <div className="mini-tutorial-pulse"></div>
                </div>
            )}

            {/* Подсказка */}
            <div 
                className={`mini-tutorial-tooltip ${arrowClass}`}
                style={tooltipStyle}
            >
                {/* Прогресс */}
                <div className="mini-tutorial-progress">
                    <div 
                        className="mini-tutorial-progress-bar"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>

                {/* Счётчик */}
                <div className="mini-tutorial-counter">
                    {currentStepIndex + 1} / {steps.length}
                </div>

                {/* Заголовок */}
                <h4 className="mini-tutorial-title">{currentStep?.title}</h4>

                {/* Контент */}
                <p className="mini-tutorial-content">{currentStep?.content}</p>

                {/* Кнопки навигации */}
                <div className="mini-tutorial-nav">
                    {!isFirstStep && (
                        <button 
                            className="mini-tutorial-btn mini-tutorial-btn-prev"
                            onClick={prevStep}
                        >
                            ← Назад
                        </button>
                    )}
                    
                    <button 
                        className={`mini-tutorial-btn ${isLastStep ? 'mini-tutorial-btn-finish' : 'mini-tutorial-btn-next'}`}
                        onClick={nextStep}
                    >
                        {isLastStep ? 'Завершить ✓' : 'Далее →'}
                    </button>
                </div>

                {/* Пропустить */}
                <button 
                    className="mini-tutorial-skip"
                    onClick={stopTutorial}
                >
                    Пропустить обучение
                </button>
            </div>
        </div>
    );
}
